/**
 * Data hook + access gate for the Chat Debug Explorer.
 *
 * The report shape mirrors `ChatDebugReport` from `@taqat/services`
 * (chat-debug-service). It is duplicated here as a type-only definition because
 * the client does not depend on `@taqat/services`; keep the two in sync.
 */

import { useEffect, useRef } from "react";
import { useHttp } from "@/hooks/use-http";
import { isAdminUser } from "@/utils/auth-storage";
import { ENABLE_DEBUG_EXPLORER, IS_DEV } from "@/lib/env";

/**
 * Whether the current user/build may open the debug explorer. The trigger
 * (header icon, keyboard shortcut, `?debug=1`) is hidden/inert unless this
 * passes. The server independently enforces the same admin/owner gate.
 */
export function canUseDebugExplorer(): boolean {
  return IS_DEV || ENABLE_DEBUG_EXPLORER || isAdminUser();
}

export interface TokenStats {
  input: number;
  output: number;
  total: number;
}

export interface DebugEvent {
  id: string;
  type: string;
  status: string | null;
  name: string | null;
  step: number | null;
  ts: string | null;
  endedAt: string | null;
  durationMs: number | null;
  toolName: string | null;
  toolCallId: string | null;
  input: unknown;
  output: unknown;
  errorMessage: string | null;
  errorStack: string | null;
  tokensIn: number | null;
  tokensOut: number | null;
  metadata: unknown;
}

export interface DebugTurn {
  requestId: string;
  model: string | null;
  status: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationMs: number | null;
  tokens: TokenStats;
  costPcu: number;
  hasError: boolean;
  retryCount: number;
  events: DebugEvent[];
}

export interface DebugMessage {
  id: string;
  role: string;
  createdAt: string | null;
  model: string | null;
  tokens: TokenStats | null;
  partial: boolean;
  error: string | null;
  parts: unknown;
}

export interface DebugCompaction {
  id: string;
  afterIndex: number | null;
  afterMessageId: string | null;
  tailStartMessageId: string | null;
  summary: string;
  messageCount: number;
  keptCount: number | null;
  beforeTokens: number;
  afterTokens: number;
  savedTokens: number;
  reductionPct: number;
  estimated: boolean;
  createdAt: string | null;
}

export interface ChatDebugReport {
  chatId: string;
  parentChatId: string | null;
  title: string | null;
  agentId: string | null;
  platform: string | null;
  generatedAt: string;
  totals: {
    tokens: TokenStats;
    costPcu: number;
    turns: number;
    durationMs: number | null;
    models: { model: string; turns: number }[];
    messageCount: number;
    toolCalls: number;
    errors: number;
    compactions: number;
    traceCount: number;
  };
  timeline: DebugTurn[];
  messages: DebugMessage[];
  compactions: DebugCompaction[];
  subAgents: ChatDebugReport[];
  notes: string[];
}

/**
 * Fetches the merged debug report for a chat. Lazily fires once when `enabled`
 * flips true (i.e. the panel opens) so we never fetch for chats the user isn't
 * inspecting. Re-arms when the panel closes; `refetch` forces a reload.
 */
export function useChatDebug(chatId: string, enabled: boolean) {
  const { data, loading, error, execute } = useHttp<{
    success: boolean;
    data: ChatDebugReport;
    message?: string;
  }>(`/chat/${chatId}/debug`, {
    method: "GET",
    requiresAuth: true,
    showErrorToast: true,
    showSuccessToast: false,
  });

  // `execute` is a fresh callback each render, so guard with a ref to fire only
  // once per open/chat rather than on every render.
  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!enabled || !chatId) {
      firedFor.current = null;
      return;
    }
    if (firedFor.current === chatId) return;
    firedFor.current = chatId;
    execute().catch(() => {
      /* surfaced via error state */
    });
  }, [enabled, chatId, execute]);

  return {
    data: data?.data ?? null,
    isLoading: loading && !data,
    isFetching: loading,
    isError: !!error,
    error,
    refetch: execute,
  };
}
